import React, { useState, useEffect, useCallback } from "react";
import { ComponentI } from "../../../../types";
import Card from "../../../../componentLibrary/Card";
import Input from "../../../../componentLibrary/Input";
import RadioGroup, {
  RadioGroupOption,
} from "../../../../componentLibrary/RadioGroup";
import { CopyVariableButton } from "../../utils/CopyVariableButton";

export default function RatingScaleContentCard({
  component,
  handleContentChange,
}: {
  component: ComponentI;
  handleContentChange: (
    id: string,
    field: string,
    value: string | number,
  ) => void;
}) {
  const [selectedScale, setSelectedScale] = useState<"0 to 10" | "1 to 5">(
    component.content.scaleType || "1 to 5",
  );

  const getScaleValues = useCallback((scaleType) => {
    switch (scaleType) {
      case "0 to 10":
        return {
          minimumValue: 0,
          maximumValue: 10,
          numberOfSteps: 11,
        };
      case "1 to 5":
        return {
          minimumValue: 1,
          maximumValue: 5,
          numberOfSteps: 5,
        };
      default:
        return {
          minimumValue: 1,
          maximumValue: 5,
          numberOfSteps: 5,
        };
    }
  }, []);

  useEffect(() => {
    const { minimumValue, maximumValue, numberOfSteps } =
      getScaleValues(selectedScale);

    if (component.content.minimumValue !== minimumValue) {
      handleContentChange(component._id, "minimumValue", minimumValue);
    }
    if (component.content.maximumValue !== maximumValue) {
      handleContentChange(component._id, "maximumValue", maximumValue);
    }
    if (component.content.numberOfSteps !== numberOfSteps) {
      handleContentChange(component._id, "numberOfSteps", numberOfSteps);
    }
  }, [
    selectedScale,
    component.content,
    component._id,
    handleContentChange,
    getScaleValues,
  ]);

  const handleScaleChange = (option: RadioGroupOption) => {
    const newScale = option.value as "1 to 5" | "0 to 10";
    setSelectedScale(newScale);
    handleContentChange(component._id, "scaleType", newScale);
  };

  return (
    <Card>
      <div className="space-y-2">
        <RadioGroup
          selectedValue={selectedScale}
          onChange={handleScaleChange}
          options={[
            { label: "1 to 5", value: "1 to 5" },
            { label: "0 to 10", value: "0 to 10" },
          ]}
        />
        <Input
          value={component.content.label}
          onChange={(e) =>
            handleContentChange(component._id, "label", e.target.value)
          }
          label="Label"
          placeholder="How would you rate your..."
          required
        />
        <Input
          value={component.content.minimumLabel || ""}
          onChange={(e) =>
            handleContentChange(component._id, "minimumLabel", e.target.value)
          }
          label="Minimum Label"
          placeholder="Least"
        />
        <Input
          value={component.content.centerLabel || ""}
          onChange={(e) =>
            handleContentChange(component._id, "centerLabel", e.target.value)
          }
          label="Center Label (Optional)"
          placeholder="Neutral"
        />
        <Input
          value={component.content.maximumLabel || ""}
          onChange={(e) =>
            handleContentChange(component._id, "maximumLabel", e.target.value)
          }
          label="Maximum Label"
          placeholder="Most"
        />
        <Input
          value={component.content.variable}
          onChange={(e) =>
            handleContentChange(component._id, "variable", e.target.value)
          }
          label="Variable Name"
          placeholder="variable_name"
          required
          TopRightElement={
            <CopyVariableButton type="icon" component={component} />
          }
        />
      </div>
    </Card>
  );
}
